import { z } from 'zod';

import { parseStatusFilters, serializeStatusFilters } from '../services/analysis-schemas';
import {
  ANALYSIS_COLUMN_KEYS,
  ANALYSIS_SORT_KEYS,
  type AnalysisSortKey,
} from './analysis-column-keys';

export const ANALYSIS_TABS = [
  'instalacao',
  'tetrazolio',
  'canteiro-areia',
  'germinacao',
  'envelhecimento',
] as const;

export type AnalysisTab = (typeof ANALYSIS_TABS)[number];

export const DEADLINE_FILTERS = ['hoje', 'atrasadas'] as const;

export type DeadlineFilter = (typeof DEADLINE_FILTERS)[number];

export const DEFAULT_ANALYSIS_TAB: AnalysisTab = 'tetrazolio';
export const FIRST_PAGE = 1;

export const DEFAULT_SORT_BY_TAB: Record<AnalysisTab, AnalysisSortKey> = {
  instalacao: ANALYSIS_COLUMN_KEYS.sampleNumber,
  tetrazolio: ANALYSIS_COLUMN_KEYS.requester,
  'canteiro-areia': ANALYSIS_COLUMN_KEYS.requester,
  germinacao: ANALYSIS_COLUMN_KEYS.sampleNumber,
  envelhecimento: ANALYSIS_COLUMN_KEYS.sampleNumber,
};

export const analysisListSearchSchema = z.object({
  tab: z.enum(ANALYSIS_TABS).catch(DEFAULT_ANALYSIS_TAB).default(DEFAULT_ANALYSIS_TAB),
  page: z.number().int().positive().catch(FIRST_PAGE).default(FIRST_PAGE),
  search: z.union([z.string(), z.number()]).transform(String).optional().catch(undefined),
  status: z
    .string()
    .optional()
    .transform((value) => serializeStatusFilters(parseStatusFilters(value))),
  determinations: z.string().optional(),
  deadline: z.enum(DEADLINE_FILTERS).optional().catch(undefined),
  from: z.string().optional(),
  to: z.string().optional(),
  sort: z.enum(ANALYSIS_SORT_KEYS).optional().catch(undefined),
});

export type AnalysisListSearch = z.infer<typeof analysisListSearchSchema>;

export const analysisDetailSearchSchema = analysisListSearchSchema.partial();

export type AnalysisDetailSearch = z.infer<typeof analysisDetailSearchSchema>;

const FILTER_KEYS = ['search', 'status', 'determinations', 'deadline', 'from', 'to'] as const;

export function resolveSort(search: AnalysisListSearch): AnalysisSortKey {
  return search.sort ?? DEFAULT_SORT_BY_TAB[search.tab];
}

export function hasActiveFilters(search: AnalysisListSearch): boolean {
  const hasFilterValue = FILTER_KEYS.some((key) => search[key] !== undefined);

  return hasFilterValue || resolveSort(search) !== DEFAULT_SORT_BY_TAB[search.tab];
}

export function clearedFilters(): Partial<AnalysisListSearch> {
  const cleared = [...FILTER_KEYS, 'sort'].map((key) => [key, undefined]);

  return { ...Object.fromEntries(cleared), page: FIRST_PAGE };
}

export function toDetailSearch(listSearch: AnalysisListSearch): AnalysisDetailSearch {
  const sort = resolveSort(listSearch);

  return {
    tab: listSearch.tab,
    page: listSearch.page === FIRST_PAGE ? undefined : listSearch.page,
    search: listSearch.search,
    status: listSearch.status,
    determinations: listSearch.determinations,
    deadline: listSearch.deadline,
    from: listSearch.from,
    to: listSearch.to,
    sort: sort === DEFAULT_SORT_BY_TAB[listSearch.tab] ? undefined : sort,
  };
}

export function toListSearch(detailSearch: AnalysisDetailSearch, fallbackTab: AnalysisTab) {
  return { ...detailSearch, tab: detailSearch.tab ?? fallbackTab };
}
